import { pnum } from "../converters/numberInput";
import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NoticeBox } from "@/components/NoticeBox";
import { FirstPaymentDateField, useOptionalStartDate } from "../converters/FirstPaymentDateField";
import { formatFullDate, termDates } from "../converters/paymentDates";

const TOOL_INFO_CONTENT = {
  what: `Calculates the monthly contribution needed to reach a college savings goal by a target date, factoring in your current savings and an expected rate of return.`,
  how: `Enter your savings goal, current savings, years until college, and expected annual return, and the required monthly contribution is calculated. Add the date of your first contribution to see your last contribution and the date your goal is reached; it only labels the plan and never changes an amount.`,
  tip: `This is a general savings projection, not specific to any one state's 529 plan — tax benefits and investment options vary by state, so check your specific plan's terms for the tax-advantage details.`,
};

export default function College529Calculator() {
  const [goal, setGoal] = useState("100000");
  const [current, setCurrent] = useState("5000");
  const [yearsToCollege, setYearsToCollege] = useState("15");
  const [returnRate, setReturnRate] = useState("6");
  const startState = useOptionalStartDate();
  const start = startState.start;

  const result = useMemo(() => {
    const G = pnum(goal || "0");
    const C = pnum(current || "0");
    const yrsRaw = parseInt(yearsToCollege, 10);
    const yearsInvalid = !Number.isFinite(yrsRaw) || yrsRaw < 1;
    const years = Math.max(1, yrsRaw || 1);
    const months = years * 12;
    const r = pnum(returnRate || "0") / 100 / 12;

    // future value of current savings
    const fvCurrent = C * Math.pow(1 + r, months);
    const remainingNeeded = Math.max(0, G - fvCurrent);

    // required monthly contribution: FV of annuity formula solved for payment
    const requiredMonthly =
      r === 0
        ? remainingNeeded / months
        : (remainingNeeded * r) / (Math.pow(1 + r, months) - 1);

    return { fvCurrent, remainingNeeded, requiredMonthly, yearsInvalid, negativeReturn: r < 0, lumpSum: Math.max(0, G - C), months };
  }, [goal, current, yearsToCollege, returnRate]);

  const { blocked } = useLogToolUsage(!result.yearsInvalid, 3000);


  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block text-sm">Savings Goal ($)</Label>
            <Input aria-label="Savings Goal ($)" value={goal} onChange={e => setGoal(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Current Savings ($)</Label>
            <Input aria-label="Current Savings ($)" value={current} onChange={e => setCurrent(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Years Until College</Label>
            <Input aria-label="Years Until College"
              aria-invalid={result.yearsInvalid || undefined}
              inputMode="numeric"
              value={yearsToCollege}
              onChange={e => setYearsToCollege(e.target.value)}
            />
            {result.yearsInvalid && <p role="alert" className="text-xs text-destructive mt-1">Enter at least 1 year. With college starting now, you would need the full remaining amount as a lump sum.</p>}
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Expected Annual Return (%)
            </Label>
            <Input
              aria-label="Expected annual return (%)"
              value={returnRate}
              onChange={e => setReturnRate(e.target.value)}
            />
            {result.negativeReturn && <NoticeBox className="text-xs mt-1" role="alert">A negative return means your savings shrink over time. Check this is what you intend; most plans assume 0% or more.</NoticeBox>}
          </div>
          <FirstPaymentDateField id="c529-start" label="First contribution date (optional)" state={startState} help="Leave empty to show only the monthly amount. With a date, the result also shows your last contribution and the date your goal is reached. The date only labels the plan; the amounts do not change." />
        </div>

        <div className="space-y-3">
          {blocked ? (
            <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
          ) : (
            <>
              <Card className="p-6 text-center">
                {result.yearsInvalid ? (
                  <>
                    <p className="text-lg font-bold text-primary">Enter at least 1 year</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Lump sum still needed today: ${result.lumpSum.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-primary">
                      ${result.requiredMonthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Monthly contribution needed
                    </p>
                    {start && (() => { const t = termDates(start, result.months / 12); return t && (
                      <p className="text-xs text-muted-foreground mt-2" data-testid="c529-dates">
                        First contribution {formatFullDate(t.first)}. Last contribution {formatFullDate(t.last)}. Goal reached on {formatFullDate(t.end)}.
                      </p>
                    ); })()}
                  </>
                )}
              </Card>
              <Card className="p-4 text-center">
                <p className="text-lg font-bold">
                  $
                  {result.fvCurrent.toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your current savings will grow to this by then
                </p>
              </Card>
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        This is a general savings projection, not specific to any one state's
        529 plan — tax benefits and investment options vary by state, so check
        your specific plan for exact terms.
      </p>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
